<script setup>
import { isNil } from 'lodash'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import HomePageApiHandler from '@/api/homepage'
import { formatDate } from '@/utils/date'
import { handleError } from '@/utils/error-handling'
import {
  AI_REPORT_DEBUG_CALL_KEYS,
  AI_REPORT_DEBUG_CALL_KINDS,
  AI_REPORT_DEBUG_CALL_OUTCOME_LABEL_KEYS,
  AI_REPORT_DEBUG_CALL_OUTCOMES,
  AI_REPORT_DEBUG_CALLS_RESPONSE_KEYS,
  AI_REPORT_DEBUG_COST_CURRENCY,
  AI_REPORT_DEBUG_EFFORT_LABEL_KEYS,
  AI_REPORT_DEBUG_GENERATION_KEYS,
  AI_REPORT_DEBUG_GENERATION_OUTCOME_LABEL_KEYS,
  AI_REPORT_DEBUG_TOTAL_KEYS
} from '@/utils/home-page/ai-report'
import { memoizeFormatNumber } from '@/utils/memoizations'
import { useLoadingState } from '@/utils/use-loading-state'

import {
  HOMEPAGE_AI_REPORT_DEBUG_CALL_ANSWER_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_CALL_COST_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_CALL_NO_ANSWER_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_CALL_OUTCOME_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_CALL_SYSTEM_PROMPT_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_CALL_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_CALL_USER_PROMPT_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_CALLS_EMPTY_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_CALLS_LOADER_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_CALLS_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_GENERATION_COST_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_GENERATION_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_TOTAL_COST_TEST_ID
} from '@/components/home-page/jest-helpers'
import AppAccordion from '@/components/ui/AppAccordion/AppAccordion'
import LoadingCircle from '@/components/ui/LoadingCircle/LoadingCircle'

defineOptions({
  name: 'HomePageAiReportDebugCalls'
})

/*
 * The last generation of the Alignment report of a selection with its calls, and the total cost
 * of the Organization (FR-3.2, FR-5.5, FR-5.6). While a generation runs, the server serves the
 * last finished one (FR-3.6).
 */
const props = defineProps({
  groupId: {
    type: [String, Number],
    default: null
  },

  accountId: {
    type: [String, Number],
    default: null
  }
})

const { t } = useI18n()

const { GENERATION, TOTAL } = AI_REPORT_DEBUG_CALLS_RESPONSE_KEYS

const STARTED_AT_FORMAT = 'YYYY-MM-DD HH:mm'

const EMPTY_VALUE = '–'

const MS_IN_SECOND = 1000

// a call of a few tokens costs fractions of a cent
const COST_MAX_FRACTION_DIGITS = 6

const homepageApi = new HomePageApiHandler()

const { isLoading, toggleLoading } = useLoadingState()

const callsData = ref(null)

// the open state of each call row, by its index
const openCalls = ref({})

// the dialog mounts this view anew on each open, so the calls are read on each open
const loadCalls = async () => {
  toggleLoading(true)

  try {
    callsData.value = await homepageApi.getAiReportDebugCalls({
      groupId: props.groupId,
      accountId: props.accountId
    })
  } catch (error) {
    handleError({ error })
  } finally {
    toggleLoading(false)
  }
}

onMounted(async () => {
  await loadCalls()
})

const formatCost = ({ value, currency = AI_REPORT_DEBUG_COST_CURRENCY }) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: COST_MAX_FRACTION_DIGITS
  }).format(value)
}

// "no price" for a model without a fixed price (FR-5.8); a dash without token counts (FR-5.4)
const getCostText = ({ cost, priced }) => {
  if (!priced) {
    return t('homepage.ai_reports.debug.no_price')
  }

  return isNil(cost) ? EMPTY_VALUE : formatCost({ value: cost })
}

const formatTokens = value => {
  return isNil(value) ? EMPTY_VALUE : memoizeFormatNumber({ value })
}

const getTokensText = ({ inputTokens, outputTokens }) => {
  return t('homepage.ai_reports.debug.tokens', {
    input: formatTokens(inputTokens),
    output: formatTokens(outputTokens)
  })
}

const getDurationText = durationMs => {
  if (isNil(durationMs)) {
    return EMPTY_VALUE
  }

  return t('homepage.ai_reports.debug.duration', {
    seconds: (durationMs / MS_IN_SECOND).toFixed(1)
  })
}

const getEffortText = effort => {
  const labelKey = AI_REPORT_DEBUG_EFFORT_LABEL_KEYS[effort]

  return labelKey ? t(labelKey) : effort
}

const getOutcomeText = ({ outcome, reason, labelKeys }) => {
  const labelKey = labelKeys[outcome]
  const outcomeText = labelKey ? t(labelKey) : outcome

  if (!reason) {
    return outcomeText
  }

  return t('homepage.ai_reports.debug.outcome_with_reason', { outcome: outcomeText, reason })
}

const totalText = computed(() => {
  const total = callsData.value?.[TOTAL] ?? {}
  const currency = total[AI_REPORT_DEBUG_TOTAL_KEYS.CURRENCY] ?? AI_REPORT_DEBUG_COST_CURRENCY

  return t('homepage.ai_reports.debug.total_cost', {
    cost: formatCost({ value: total[AI_REPORT_DEBUG_TOTAL_KEYS.COST] ?? 0, currency }),
    count: total[AI_REPORT_DEBUG_TOTAL_KEYS.GENERATIONS] ?? 0
  })
})

const generation = computed(() => callsData.value?.[GENERATION] ?? null)

const generationView = computed(() => {
  if (!generation.value) {
    return null
  }

  const {
    STARTED_AT,
    MODEL,
    EFFORT,
    TIME_LIMIT_SECONDS,
    PAIRS_PER_CALL,
    ELEMENTS_PER_CALL,
    PROMPTS_EDITED,
    OUTCOME,
    REASON,
    INPUT_TOKENS,
    OUTPUT_TOKENS,
    COST,
    PRICED,
    DURATION_MS
  } = AI_REPORT_DEBUG_GENERATION_KEYS

  const startedAt = generation.value[STARTED_AT]

  return {
    startedAt: isNil(startedAt)
      ? EMPTY_VALUE
      : t('homepage.ai_reports.debug.started_at', {
          date: formatDate(startedAt, STARTED_AT_FORMAT)
        }),

    model: generation.value[MODEL],
    effort: getEffortText(generation.value[EFFORT]),

    outcome: getOutcomeText({
      outcome: generation.value[OUTCOME],
      reason: generation.value[REASON],
      labelKeys: AI_REPORT_DEBUG_GENERATION_OUTCOME_LABEL_KEYS
    }),

    tokens: getTokensText({
      inputTokens: generation.value[INPUT_TOKENS],
      outputTokens: generation.value[OUTPUT_TOKENS]
    }),

    cost: t('homepage.ai_reports.debug.cost', {
      cost: getCostText({ cost: generation.value[COST], priced: generation.value[PRICED] })
    }),

    duration: getDurationText(generation.value[DURATION_MS]),

    settings: t('homepage.ai_reports.debug.generation_settings', {
      timeLimit: generation.value[TIME_LIMIT_SECONDS],
      pairs: generation.value[PAIRS_PER_CALL],
      elements: generation.value[ELEMENTS_PER_CALL]
    }),

    promptsEdited: Boolean(generation.value[PROMPTS_EDITED])
  }
})

// the calls in the served order: the batches by their number, then the summary call (FR-3.2)
const callRows = computed(() => {
  const calls = generation.value?.[AI_REPORT_DEBUG_GENERATION_KEYS.CALLS] ?? []

  const {
    INDEX,
    KIND,
    MODEL,
    EFFORT,
    SYSTEM_PROMPT,
    USER_PROMPT,
    ANSWER,
    OUTCOME,
    REASON,
    INPUT_TOKENS,
    OUTPUT_TOKENS,
    COST,
    PRICED,
    DURATION_MS
  } = AI_REPORT_DEBUG_CALL_KEYS

  return calls.map(call => {
    const index = call[INDEX]
    const outcome = call[OUTCOME]

    const title =
      call[KIND] === AI_REPORT_DEBUG_CALL_KINDS.SUMMARY
        ? t('homepage.ai_reports.debug.call_kind.summary')
        : t('homepage.ai_reports.debug.call_kind.batch', { number: index })

    return {
      index,
      title,
      model: call[MODEL],
      effort: getEffortText(call[EFFORT]),
      systemPrompt: call[SYSTEM_PROMPT],
      userPrompt: call[USER_PROMPT],
      answer: call[ANSWER],

      // a call that timed out or failed before an answer has none (FR-3.4)
      hasAnswer: !isNil(call[ANSWER]),

      outcome: getOutcomeText({
        outcome,
        reason: call[REASON],
        labelKeys: AI_REPORT_DEBUG_CALL_OUTCOME_LABEL_KEYS
      }),

      isSuccess: outcome === AI_REPORT_DEBUG_CALL_OUTCOMES.SUCCESS,

      tokens: getTokensText({
        inputTokens: call[INPUT_TOKENS],
        outputTokens: call[OUTPUT_TOKENS]
      }),

      cost: getCostText({ cost: call[COST], priced: call[PRICED] }),
      duration: getDurationText(call[DURATION_MS])
    }
  })
})
</script>

<template>
  <div
    :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_CALLS_TEST_ID"
    :data-testid="HOMEPAGE_AI_REPORT_DEBUG_CALLS_TEST_ID"
    class="hardc-Calls"
  >
    <div class="hardc-Heading">
      {{ t('homepage.ai_reports.debug.section.calls') }}
    </div>

    <div
      v-if="isLoading"
      :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_CALLS_LOADER_TEST_ID"
      :data-testid="HOMEPAGE_AI_REPORT_DEBUG_CALLS_LOADER_TEST_ID"
      class="hardc-Loader"
    >
      <LoadingCircle />
    </div>

    <template v-else-if="callsData">
      <div
        :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_TOTAL_COST_TEST_ID"
        :data-testid="HOMEPAGE_AI_REPORT_DEBUG_TOTAL_COST_TEST_ID"
        class="hardc-Total"
      >
        {{ totalText }}
      </div>

      <div
        v-if="!generationView"
        :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_CALLS_EMPTY_TEST_ID"
        :data-testid="HOMEPAGE_AI_REPORT_DEBUG_CALLS_EMPTY_TEST_ID"
        class="hardc-Empty"
      >
        {{ t('homepage.ai_reports.debug.no_generation') }}
      </div>

      <template v-else>
        <div
          :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_GENERATION_TEST_ID"
          :data-testid="HOMEPAGE_AI_REPORT_DEBUG_GENERATION_TEST_ID"
          class="hardc-Generation"
        >
          <div class="hardc-Generation_Line">
            <span class="hardc-Generation_Date">{{ generationView.startedAt }}</span>
            <span>{{ generationView.model }}</span>
            <span>{{ generationView.effort }}</span>
            <span class="hardc-Generation_Outcome">{{ generationView.outcome }}</span>
          </div>
          <div class="hardc-Generation_Line hardc-Generation_Line-secondary">
            <span>{{ generationView.tokens }}</span>
            <span
              :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_GENERATION_COST_TEST_ID"
              :data-testid="HOMEPAGE_AI_REPORT_DEBUG_GENERATION_COST_TEST_ID"
            >
              {{ generationView.cost }}
            </span>
            <span>{{ generationView.duration }}</span>
            <span>{{ generationView.settings }}</span>
            <span v-if="generationView.promptsEdited">
              {{ t('homepage.ai_reports.debug.prompts_edited') }}
            </span>
          </div>
        </div>

        <AppAccordion
          v-for="callRow in callRows"
          :key="callRow.index"
          v-model:open="openCalls[callRow.index]"
          :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_CALL_TEST_ID"
          :data-testid="HOMEPAGE_AI_REPORT_DEBUG_CALL_TEST_ID"
        >
          <template #title>
            <span class="hardc-Call_Title">{{ callRow.title }}</span>
            <span
              :class="{ 'hardc-Call_Outcome-failed': !callRow.isSuccess }"
              :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_CALL_OUTCOME_TEST_ID"
              :data-testid="HOMEPAGE_AI_REPORT_DEBUG_CALL_OUTCOME_TEST_ID"
              class="hardc-Call_Outcome"
            >
              {{ callRow.outcome }}
            </span>
          </template>

          <template #header-right>
            <div class="hardc-Call_Figures">
              <span>{{ callRow.tokens }}</span>
              <span>{{ callRow.duration }}</span>
              <span
                :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_CALL_COST_TEST_ID"
                :data-testid="HOMEPAGE_AI_REPORT_DEBUG_CALL_COST_TEST_ID"
                class="hardc-Call_Cost"
              >
                {{ callRow.cost }}
              </span>
            </div>
          </template>

          <template #content>
            <div class="hardc-Call_Content">
              <div class="hardc-Call_Meta">
                <span>{{ callRow.model }}</span>
                <span>{{ callRow.effort }}</span>
              </div>

              <div class="hardc-Call_Label">
                {{ t('homepage.ai_reports.debug.label.call_system_prompt') }}
              </div>
              <pre
                :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_CALL_SYSTEM_PROMPT_TEST_ID"
                :data-testid="HOMEPAGE_AI_REPORT_DEBUG_CALL_SYSTEM_PROMPT_TEST_ID"
                class="hardc-Call_Text"
                >{{ callRow.systemPrompt }}</pre
              >

              <div class="hardc-Call_Label">
                {{ t('homepage.ai_reports.debug.label.call_user_prompt') }}
              </div>
              <pre
                :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_CALL_USER_PROMPT_TEST_ID"
                :data-testid="HOMEPAGE_AI_REPORT_DEBUG_CALL_USER_PROMPT_TEST_ID"
                class="hardc-Call_Text"
                >{{ callRow.userPrompt }}</pre
              >

              <div class="hardc-Call_Label">
                {{ t('homepage.ai_reports.debug.label.answer') }}
              </div>
              <pre
                v-if="callRow.hasAnswer"
                :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_CALL_ANSWER_TEST_ID"
                :data-testid="HOMEPAGE_AI_REPORT_DEBUG_CALL_ANSWER_TEST_ID"
                class="hardc-Call_Text"
                >{{ callRow.answer }}</pre
              >
              <div
                v-else
                :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_CALL_NO_ANSWER_TEST_ID"
                :data-testid="HOMEPAGE_AI_REPORT_DEBUG_CALL_NO_ANSWER_TEST_ID"
                class="hardc-Call_NoAnswer"
              >
                {{ t('homepage.ai_reports.debug.no_answer') }}
              </div>
            </div>
          </template>
        </AppAccordion>
      </template>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.hardc-Calls {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: $system-ui;
}

.hardc-Heading {
  color: $dark-1;
  font-size: $fs-16;
  font-weight: fw('bold');
  line-height: 24px;
}

.hardc-Loader {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
}

.hardc-Total {
  color: $dark-1;
  font-size: $fs-14;
  font-weight: fw('semi-bold');
  line-height: 20px;
}

.hardc-Empty {
  color: $dark-3;
  font-size: $fs-14;
  line-height: 20px;
}

.hardc-Generation {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;
  border-radius: $border-radius-md-next;
  background-color: $grey-3-next;
}

.hardc-Generation_Line {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  color: $dark-1;
  font-size: $fs-14;
  line-height: 20px;

  &-secondary {
    color: $dark-3;
  }
}

.hardc-Generation_Date,
.hardc-Generation_Outcome {
  font-weight: fw('semi-bold');
}

.hardc-Call_Title {
  margin-right: 12px;
}

.hardc-Call_Outcome {
  color: $grade-high-color-next;
  font-size: $fs-14;

  &-failed {
    color: $grade-low-color-next;
  }
}

.hardc-Call_Figures {
  display: flex;
  gap: 16px;
  color: $dark-3;
  font-size: $fs-14;
  font-weight: fw('regular');
}

.hardc-Call_Cost {
  color: $dark-1;
}

.hardc-Call_Content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
}

.hardc-Call_Meta {
  display: flex;
  gap: 16px;
  color: $dark-3;
  font-size: $fs-14;
  line-height: 20px;
}

.hardc-Call_Label {
  color: $dark-1;
  font-size: $fs-14;
  font-weight: fw('semi-bold');
  line-height: 20px;
}

.hardc-Call_Text {
  max-height: 320px;
  margin: 0;
  padding: 8px 12px;
  overflow: auto;
  border-radius: $border-radius-sm-next;
  background-color: $grey-3-next;
  color: $dark-1;
  font-size: $fs-12;
  line-height: 18px;
  white-space: pre-wrap;
  word-break: break-word;
}

.hardc-Call_NoAnswer {
  color: $dark-3;
  font-size: $fs-14;
  line-height: 20px;
}
</style>
